// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	canReportMessage,
	createMessageActionHandlers,
	getCopyableMessageText,
	getEffectiveContent,
	isClientSystemMessage,
	isEmbedsSuppressed,
	type MessageActionHandlers,
	type MessagePermissions,
	requestSpeakMessage,
	useMessagePermissions,
} from '@app/features/channel/components/MessageActionUtils';
import {useQuickReactionEmojis} from '@app/features/channel/state/QuickReactionStore';
import * as ChannelUtils from '@app/features/channel/utils/ChannelUtils';
import {messageAgentSources} from '@app/features/channel/utils/MessageAgentFiles';
import {MessageDebugModal} from '@app/features/devtools/components/debug/MessageDebugModal';
import type {FlatEmoji} from '@app/features/emoji/types/EmojiTypes';
import Guilds from '@app/features/guild/state/Guilds';
import {
	ADD_REACTION_DESCRIPTOR,
	BOOKMARK_MESSAGE_DESCRIPTOR,
	COPY_MESSAGE_ID_DESCRIPTOR,
	COPY_MESSAGE_LINK_DESCRIPTOR,
	DELETE_MESSAGE_DESCRIPTOR,
	EDIT_MESSAGE_DESCRIPTOR,
	MARK_AS_UNREAD_DESCRIPTOR,
	PIN_MESSAGE_DESCRIPTOR,
	REMOVE_BOOKMARK_DESCRIPTOR,
	REPLY_DESCRIPTOR,
	REPORT_MESSAGE_DESCRIPTOR,
	SUPPRESS_EMBEDS_DESCRIPTOR,
	TRY_AGAIN_DESCRIPTOR,
	UNPIN_MESSAGE_DESCRIPTOR,
} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {canSendToAgent, sendToAgent} from '@app/features/konvo_agents/state/SendToAgent';
import {fetchAgentFiles} from '@app/features/konvo_agents/utils/AgentSourceFiles';
import {
	askKonvoAboutMessage,
	createEventFromMessage,
	suggestReminderFromMessage,
	summariseThread,
} from '@app/features/konvo_assistant/commands/AssistantMessageActions';
import AssistantSuggestions from '@app/features/konvo_assistant/state/AssistantSuggestions';
import {askAboutKonvoChat} from '@app/features/konvo_panel/state/AskAboutChat';
import {KonvoRemindMeModal} from '@app/features/konvo_reminders/components/KonvoRemindMeModal';
import KonvoChannelScratchpad from '@app/features/konvo_scratchpad/state/KonvoChannelScratchpad';
import KonvoScratchpad from '@app/features/konvo_scratchpad/state/KonvoScratchpad';
import {KonvoCreateTaskModal} from '@app/features/messaging/components/modals/KonvoCreateTaskModal';
import {useMessageReactions as useMessageReactionsSnapshot} from '@app/features/messaging/hooks/useMessageReactionStore';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import SavedMessages from '@app/features/messaging/state/SavedMessages';
import {messagePreviewText} from '@app/features/messaging/utils/MessagePreviewText';
import {openReportMessageModal} from '@app/features/moderation/utils/ReportActionUtils';
import Navigation from '@app/features/navigation/state/Navigation';
import Permission from '@app/features/permissions/state/Permission';
import {
	AddReactionIcon,
	BookmarkIcon,
	CopyIdIcon,
	CopyLinkIcon,
	CopyMessageTextIcon,
	DebugMessageIcon,
	DeleteIcon,
	EditMessageIcon,
	ForwardIcon,
	MarkAsUnreadIcon,
	PinIcon,
	RemoveAllReactionsIcon,
	ReplyIcon,
	ReportMessageIcon,
	RetryIcon,
	SpeakMessageIcon,
	StopSpeakingIcon,
	SuppressEmbedsIcon,
	ViewReactionsIcon,
} from '@app/features/ui/action_menu/ContextMenuIcons';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {KonvoLogo} from '@app/features/ui/components/icons/KonvoLogo';
import {
	BellRingingIcon,
	CalendarCheckIcon,
	ChatCircleDotsIcon,
	CheckSquareIcon,
	ListBulletsIcon,
	SparkleIcon,
} from '@app/features/ui/icons/Icons';
import {ScratchpadIcon} from '@app/features/ui/icons/ScratchpadIcon';
import {KeybindHint} from '@app/features/ui/keybind_hint/KeybindHint';
import type {MenuGroupType, MenuItemType, MenuSheetItem} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import UserSettings from '@app/features/user/state/UserSettings';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import TtsUtils from '@app/features/voice/utils/VoiceTtsUtils';
import {KONVOBOT_ID} from '@konvo/constants/src/AppConstants';
import {MessageStates, Permissions} from '@konvo/constants/src/ChannelConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {IconTerminal2} from '@tabler/icons-react';
import {useCallback, useEffect, useMemo, useState} from 'react';

const KONVO_ASSISTANT_DESCRIPTOR = msg({
	message: 'Konvo Assistant',
	comment: 'Submenu in the message menu holding the assistant actions.',
});
const KONVO_ASSISTANT_EVENT_DESCRIPTOR = msg({
	message: 'Create event…',
	comment: 'Assistant submenu item: the assistant drafts an event from this message.',
});
const KONVO_ASSISTANT_TASK_DESCRIPTOR = msg({
	message: 'Suggest a task…',
	comment: 'Assistant submenu item: the assistant drafts a task from this message.',
});
const KONVO_ASSISTANT_REMINDER_DESCRIPTOR = msg({
	message: 'Suggest a reminder…',
	comment: 'Assistant submenu item: the assistant picks a reminder time from this message.',
});
const KONVO_ASSISTANT_ASK_DESCRIPTOR = msg({
	message: 'Ask Konvo about this',
	comment: 'Assistant submenu item: sends the message to the Assistant DM.',
});
const KONVO_ASSISTANT_SUMMARISE_DESCRIPTOR = msg({
	message: 'Summarise thread so far',
	comment: 'Assistant submenu item: asks the Assistant DM for a summary of the recent chat.',
});
const KONVO_CREATE_TASK_DESCRIPTOR = msg({
	message: 'Create task',
	comment: 'Message action menu item that creates a ClickUp task from the message.',
});

const KONVO_SEND_TO_AGENT_DESCRIPTOR = msg({
	message: 'Send to an agent',
	comment: 'Message action: start a coding-agent chat (Agents) with this message quoted as context.',
});
const KONVO_SEND_TO_AGENT_SHORT_DESCRIPTOR = msg({
	message: 'Agent',
	comment: 'Short label under the round quick action that sends the message to a coding agent. One short word.',
});
const KONVO_FROM_IN_CHANNEL_DESCRIPTOR = msg({
	message: '{author} in {place}',
	comment:
		'Send to an agent: where a quoted message came from, e.g. "Markus Mulder in #konvo-dev" or "Markus Mulder in Lerato Mokoena".',
});
const KONVO_ADD_TO_SCRATCHPAD_DESCRIPTOR = msg({
	message: 'Add to Scratchpad',
	comment: 'Message action: append the message to the personal scratchpad as a to-do.',
});
const KONVO_ADD_TO_CHAT_PAD_DESCRIPTOR = msg({
	message: "Add to this chat's pad",
	comment: 'Message action: append the message to the shared scratchpad of the chat.',
});
const KONVO_REMIND_ME_DESCRIPTOR = msg({
	message: 'Remind me',
	comment: 'Message action menu item that schedules a reminder DM about the message.',
});

const MESSAGE_DEBUG_DESCRIPTOR = msg({
	message: 'Message debug',
	comment: 'Title of the developer-mode message debug modal opened from the message context menu.',
});
const VIEW_REACTIONS_DESCRIPTOR = msg({
	message: 'View reactions',
	comment: 'Message context menu item that opens the reactions list sheet for the message.',
});
const REMOVE_ALL_REACTIONS_DESCRIPTOR = msg({
	message: 'Remove all reactions',
	comment: 'Destructive message context menu item for moderators that clears every reaction on a message.',
});
const FORWARD_DESCRIPTOR = msg({
	message: 'Forward',
	comment: 'Message context menu item that opens the forward-to-channel picker.',
});
const UNSUPPRESS_EMBEDS_DESCRIPTOR = msg({
	message: 'Unsuppress embeds',
	comment: 'Message context menu item that re-shows previously hidden link previews and embeds on the message.',
});
const COPY_SHORT_DESCRIPTOR = msg({
	message: 'Copy',
	comment: 'Short label under the round Copy message button in the phone message menu. One short word.',
});
const PIN_SHORT_DESCRIPTOR = msg({
	message: 'Pin',
	comment: 'Short label under the round Pin message button in the phone message menu. One short word.',
});
const TASK_SHORT_DESCRIPTOR = msg({
	message: 'Task',
	comment: 'Short label under the round Create task button in the phone message menu. One short word.',
});
const KONVO_ASK_DESCRIPTOR = msg({
	message: 'Ask Konvo about this message',
	comment: 'Phone message menu action: opens the Ask about this chat sheet with this one message as context.',
});
const KONVO_ASK_SHORT_DESCRIPTOR = msg({
	message: 'Ask Konvo',
	comment: 'Short label under the round Ask Konvo button in the phone message menu. Konvo is the assistant’s name.',
});
const UNPIN_SHORT_DESCRIPTOR = msg({
	message: 'Unpin',
	comment: 'Short label under the round Unpin message button in the phone message menu. One short word.',
});
const COPY_MESSAGE_DESCRIPTOR = msg({
	message: 'Copy message',
	comment: 'Message context menu item that copies the message text content to the clipboard.',
});
const STOP_SPEAKING_DESCRIPTOR = msg({
	message: 'Stop speaking',
	comment: 'Message context menu item that stops the text-to-speech playback in progress.',
});
const SPEAK_MESSAGE_DESCRIPTOR = msg({
	message: 'Speak message',
	comment: 'Message context menu item that reads the message aloud via text-to-speech.',
});
const DEBUG_MESSAGE_DESCRIPTOR = msg({
	message: 'Debug message',
	comment: 'Developer-mode message context menu item that opens an internal debug view for the message.',
});

interface MessageActionMenuOptions {
	onOpenEmojiPicker?: () => void;
	onOpenReactionsSheet?: () => void;
	onClose?: () => void;
	onDelete?: (bypassConfirm?: boolean) => void;
	sourceChannel?: MessagePermissions['channel'] | null;
	quickReactionCount?: number;
	submenuReactionCount?: number;
}

export const messageActionMenuItemIds = {
	addReaction: 'add-reaction',
	viewReactions: 'view_reactions',
	removeAllReactions: 'remove_all_reactions',
	reply: 'reply',
	forward: 'forward',
	edit: 'edit',
	pinMessage: 'message_pin',
	bookmarkMessage: 'message_bookmark',
	createTask: 'konvo_create_task',
	remindMe: 'konvo_remind_me',
	addToScratchpad: 'konvo_add_to_scratchpad',
	addToChatPad: 'konvo_add_to_chat_pad',
	suppressEmbeds: 'suppress_embeds',
	markUnread: 'message_mark_unread',
	speakMessage: 'message_speak',
	deleteMessage: 'message_delete',
	reportMessage: 'report_message',
	copyMessage: 'copy_message',
	copyMessageLink: 'message_copy_link',
	copyMessageId: 'message_copy_id',
	debugMessage: 'debug_message',
	konvoAssistant: 'konvo_assistant',
	askKonvo: 'konvo_ask',
	sendToAgent: 'konvo_send_to_agent',
} as const;

/** Actions shown in the phone menu's titled Konvo and ClickUp sections. */
const KONVO_SECTION_IDS: ReadonlyArray<string> = [
	messageActionMenuItemIds.askKonvo,
	messageActionMenuItemIds.konvoAssistant,
	messageActionMenuItemIds.sendToAgent,
	messageActionMenuItemIds.addToScratchpad,
	messageActionMenuItemIds.addToChatPad,
	messageActionMenuItemIds.remindMe,
];
const CLICKUP_SECTION_IDS: ReadonlyArray<string> = [messageActionMenuItemIds.createTask];

export interface MessageActionMenuData {
	handlers: MessageActionHandlers;
	permissions: MessagePermissions | null;
	groups: Array<MenuGroupType>;
	quickReactionEmojis: Array<FlatEmoji>;
	submenuReactionEmojis: Array<FlatEmoji>;
	quickReactionRowVisible: boolean;
	isFailed: boolean;
	isSaved: boolean;
}

export const useMessageActionMenuData = (
	message: Message,
	options: MessageActionMenuOptions = {},
): MessageActionMenuData => {
	const {i18n} = useLingui();
	const {
		onOpenEmojiPicker,
		onOpenReactionsSheet,
		onClose,
		onDelete,
		sourceChannel,
		quickReactionCount = 5,
		submenuReactionCount = 16,
	} = options;
	const permissions = useMessagePermissions(message, sourceChannel);
	const handlers = useMemo(
		() => createMessageActionHandlers(message, {i18n, onClose, channel: permissions?.channel ?? sourceChannel}),
		[message, permissions?.channel, sourceChannel, onClose, i18n.locale],
	);
	const isSaved = useMemo(() => SavedMessages.isSaved(message.id), [message.id]);
	const channel = permissions?.channel ?? sourceChannel ?? null;
	const shouldBuildQuickReactions = permissions?.canAddReactions === true && message.state === MessageStates.SENT;
	const reactionEmojiLimit = Math.max(quickReactionCount, submenuReactionCount);
	const reactionEmojis = useQuickReactionEmojis(channel, reactionEmojiLimit, shouldBuildQuickReactions);
	const quickReactionEmojis = useMemo(
		() => reactionEmojis.slice(0, quickReactionCount),
		[reactionEmojis, quickReactionCount],
	);
	const submenuReactionEmojis = useMemo(
		() => reactionEmojis.slice(0, submenuReactionCount),
		[reactionEmojis, submenuReactionCount],
	);
	const developerMode = UserSettings.developerMode;
	const effectiveContent = useMemo(() => getEffectiveContent(message), [message]);
	const copyableMessageText = useMemo(() => getCopyableMessageText(message, i18n), [message, i18n.locale]);
	const canManageMessages = useMemo(
		() =>
			permissions != null &&
			!permissions.isDM &&
			Permission.can(Permissions.MANAGE_MESSAGES, {channelId: message.channelId}),
		[permissions, message.channelId],
	);
	const [isSpeaking, setIsSpeaking] = useState(TtsUtils.isSpeaking());
	const [voiceReady, setVoiceReady] = useState(TtsUtils.hasVoices());
	const supportsInteractiveActions = !isClientSystemMessage(message);
	useEffect(() => {
		if (!TtsUtils.isSupported()) {
			return;
		}
		const interval = setInterval(() => {
			setIsSpeaking(TtsUtils.isSpeaking());
			setVoiceReady(TtsUtils.hasVoices());
		}, 100);
		return () => clearInterval(interval);
	}, []);
	const handleSpeakMessage = useCallback(() => {
		requestSpeakMessage(message);
	}, [message]);
	const handleReportMessage = useCallback(() => {
		if (!canReportMessage(message)) {
			return;
		}
		if (onClose) {
			ModalCommands.runAfterBottomSheetClose(onClose, () => openReportMessageModal(message));
			return;
		}
		openReportMessageModal(message);
	}, [message, onClose]);
	const handleDebugMessage = useCallback(() => {
		const messageDebugModal = modal(() => (
			<MessageDebugModal
				title={i18n._(MESSAGE_DEBUG_DESCRIPTOR)}
				message={message}
				data-flx="channel.message-action-menu.handle-debug-message.message-debug-modal"
			/>
		));
		if (onClose) {
			ModalCommands.pushWithKeyAfterBottomSheetClose(onClose, messageDebugModal, `message-debug-${message.id}`);
			return;
		}
		ModalCommands.pushWithKey(messageDebugModal, `message-debug-${message.id}`);
	}, [message, onClose, i18n]);
	const reactions = useMessageReactionsSnapshot(message.id);
	const assistantAvailable = AssistantSuggestions.available;
	const agentsOn = canSendToAgent();
	useEffect(() => AssistantSuggestions.ensureStatus(), []);
	const inAssistantDm = channel?.isDM() === true && channel.recipientIds.includes(KONVOBOT_ID);
	const askInSheet = MobileLayout.enabled && !inAssistantDm;
	const groups = useMemo(() => {
		const reactionActions: Array<MenuItemType> = [];
		const interactionActions: Array<MenuItemType> = [];
		const managementActions: Array<MenuSheetItem> = [];
		const utilityActions: Array<MenuItemType> = [];
		if (message.state === MessageStates.SENT) {
			if (permissions?.canAddReactions && onOpenEmojiPicker) {
				reactionActions.push({
					id: messageActionMenuItemIds.addReaction,
					icon: <AddReactionIcon size={20} data-flx="channel.message-action-menu.groups.add-reaction-icon" />,
					label: i18n._(ADD_REACTION_DESCRIPTOR),
					onClick: onOpenEmojiPicker,
					shortcut: <KeybindHint action="message_react" data-flx="channel.message-action-menu.groups.keybind-hint" />,
				});
			}
			if (reactions.length > 0 && onOpenReactionsSheet) {
				reactionActions.push({
					id: messageActionMenuItemIds.viewReactions,
					icon: <ViewReactionsIcon size={20} data-flx="channel.message-action-menu.groups.view-reactions-icon" />,
					label: i18n._(VIEW_REACTIONS_DESCRIPTOR),
					onClick: onOpenReactionsSheet,
				});
			}
			if (canManageMessages && reactions.length > 0) {
				reactionActions.push({
					id: messageActionMenuItemIds.removeAllReactions,
					icon: (
						<RemoveAllReactionsIcon size={20} data-flx="channel.message-action-menu.groups.remove-all-reactions-icon" />
					),
					label: i18n._(REMOVE_ALL_REACTIONS_DESCRIPTOR),
					onClick: handlers.handleRemoveAllReactions,
					danger: true,
				});
			}
			interactionActions.push({
				id: messageActionMenuItemIds.markUnread,
				icon: <MarkAsUnreadIcon size={20} data-flx="channel.message-action-menu.groups.mark-as-unread-icon" />,
				label: i18n._(MARK_AS_UNREAD_DESCRIPTOR),
				onClick: handlers.handleMarkAsUnread,
				shortcut: (
					<KeybindHint action="message_mark_unread" data-flx="channel.message-action-menu.groups.keybind-hint--2" />
				),
			});
			if (message.isUserMessage() && supportsInteractiveActions && permissions?.canSendMessages) {
				interactionActions.push({
					id: messageActionMenuItemIds.reply,
					icon: <ReplyIcon size={20} data-flx="channel.message-action-menu.groups.reply-icon" />,
					label: i18n._(REPLY_DESCRIPTOR),
					onClick: handlers.handleReply,
					shortcut: (
						<KeybindHint action="message_reply" data-flx="channel.message-action-menu.groups.keybind-hint--3" />
					),
				});
			}
			if (message.isUserMessage() && supportsInteractiveActions && permissions?.canForwardMessage) {
				interactionActions.push({
					id: messageActionMenuItemIds.forward,
					icon: <ForwardIcon size={20} data-flx="channel.message-action-menu.groups.forward-icon" />,
					label: i18n._(FORWARD_DESCRIPTOR),
					onClick: handlers.handleForward,
					shortcut: (
						<KeybindHint action="message_forward" data-flx="channel.message-action-menu.groups.keybind-hint--4" />
					),
				});
			}
			if (message.isCurrentUserAuthor() && message.isUserMessage() && !message.messageSnapshots) {
				interactionActions.push({
					id: messageActionMenuItemIds.edit,
					icon: <EditMessageIcon size={20} data-flx="channel.message-action-menu.groups.edit-message-icon" />,
					label: i18n._(EDIT_MESSAGE_DESCRIPTOR),
					onClick: handlers.handleEditMessage,
					shortcut: <KeybindHint action="message_edit" data-flx="channel.message-action-menu.groups.keybind-hint--5" />,
				});
			}
			if (message.isUserMessage() && permissions?.canPinMessage) {
				managementActions.push({
					id: messageActionMenuItemIds.pinMessage,
					icon: <PinIcon size={20} data-flx="channel.message-action-menu.groups.pin-icon" />,
					label: message.pinned ? i18n._(UNPIN_MESSAGE_DESCRIPTOR) : i18n._(PIN_MESSAGE_DESCRIPTOR),
					quickLabel: message.pinned ? i18n._(UNPIN_SHORT_DESCRIPTOR) : i18n._(PIN_SHORT_DESCRIPTOR),
					onClick: handlers.handlePinMessage,
					shortcut: <KeybindHint action="message_pin" data-flx="channel.message-action-menu.groups.keybind-hint--6" />,
				});
			}
			if (message.isUserMessage() && supportsInteractiveActions) {
				managementActions.push({
					id: messageActionMenuItemIds.bookmarkMessage,
					icon: <BookmarkIcon size={20} filled={isSaved} data-flx="channel.message-action-menu.groups.bookmark-icon" />,
					label: isSaved ? i18n._(REMOVE_BOOKMARK_DESCRIPTOR) : i18n._(BOOKMARK_MESSAGE_DESCRIPTOR),
					onClick: handlers.handleSaveMessage(isSaved),
					shortcut: (
						<KeybindHint action="message_bookmark" data-flx="channel.message-action-menu.groups.keybind-hint--7" />
					),
				});
			}
			if (message.isUserMessage()) {
				managementActions.push({
					id: messageActionMenuItemIds.createTask,
					icon: <CheckSquareIcon size={20} data-flx="channel.message-action-menu.groups.konvo-create-task-icon" />,
					label: i18n._(KONVO_CREATE_TASK_DESCRIPTOR),
					quickLabel: i18n._(TASK_SHORT_DESCRIPTOR),
					onClick: () => {
						const openTaskModal = () => {
							ModalCommands.push(
								modal(() => <KonvoCreateTaskModal message={message} onClose={() => ModalCommands.pop()} />),
							);
						};
						if (onClose) {
							ModalCommands.runAfterBottomSheetClose(onClose, openTaskModal);
						} else {
							openTaskModal();
						}
					},
				});
			}
			if (message.isUserMessage() && assistantAvailable) {
				const after = (action: () => void) => {
					if (onClose) ModalCommands.runAfterBottomSheetClose(onClose, action);
					else action();
				};
				// Phones: Ask Konvo opens the Ask about this chat sheet with this message (a round quick
				// action); the submenu's DM handover stays for the desktop. Not in the Assistant DM itself.
				if (askInSheet) {
					managementActions.push({
						id: messageActionMenuItemIds.askKonvo,
						icon: <SparkleIcon size={20} weight="fill" />,
						label: i18n._(KONVO_ASK_DESCRIPTOR),
						quickLabel: i18n._(KONVO_ASK_SHORT_DESCRIPTOR),
						quickInk: true,
						onClick: () =>
							after(() => {
								if (!askAboutKonvoChat(message.channelId, message)) void askKonvoAboutMessage(message);
							}),
					});
				}
				managementActions.push({
					id: messageActionMenuItemIds.konvoAssistant,
					icon: <SparkleIcon size={18} weight="fill" />,
					label: i18n._(KONVO_ASSISTANT_DESCRIPTOR),
					items: [
						{
							id: 'konvo_assistant_event',
							icon: <CalendarCheckIcon size={20} />,
							label: i18n._(KONVO_ASSISTANT_EVENT_DESCRIPTOR),
							onClick: () => after(() => void createEventFromMessage(i18n, message, channel)),
						},
						{
							id: 'konvo_assistant_task',
							icon: <CheckSquareIcon size={20} />,
							label: i18n._(KONVO_ASSISTANT_TASK_DESCRIPTOR),
							onClick: () =>
								after(() =>
									ModalCommands.push(
										modal(() => <KonvoCreateTaskModal message={message} onClose={() => ModalCommands.pop()} />),
									),
								),
						},
						{
							id: 'konvo_assistant_reminder',
							icon: <BellRingingIcon size={20} />,
							label: i18n._(KONVO_ASSISTANT_REMINDER_DESCRIPTOR),
							onClick: () => after(() => void suggestReminderFromMessage(i18n, message, channel)),
						},
						...(askInSheet
							? []
							: [
									{
										id: 'konvo_assistant_ask',
										icon: <ChatCircleDotsIcon size={20} />,
										label: i18n._(KONVO_ASSISTANT_ASK_DESCRIPTOR),
										onClick: () => after(() => void askKonvoAboutMessage(message)),
									},
								]),
						...(channel
							? [
									{
										id: 'konvo_assistant_summarise',
										icon: <ListBulletsIcon size={20} />,
										label: i18n._(KONVO_ASSISTANT_SUMMARISE_DESCRIPTOR),
										onClick: () => after(() => void summariseThread(channel)),
									},
								]
							: []),
					],
				});
			}
			if (message.isUserMessage() && agentsOn) {
				managementActions.push({
					id: messageActionMenuItemIds.sendToAgent,
					icon: <IconTerminal2 size={20} stroke={1.75} />,
					label: i18n._(KONVO_SEND_TO_AGENT_DESCRIPTOR),
					quickLabel: i18n._(KONVO_SEND_TO_AGENT_SHORT_DESCRIPTOR),
					onClick: () => {
						const guild = message.guildId ? Guilds.getGuild(message.guildId) : null;
						const place =
							channel == null
								? ''
								: channel.guildId != null
									? `#${channel.name ?? ''}`
									: ChannelUtils.getDMDisplayName(channel);
						const author = message.author
							? NicknameUtils.getNickname(message.author, message.guildId, message.channelId)
							: '';
						// Mentions as names (@Sipho, #dev), not <@id>; line by line so the lines stay.
						const text = (effectiveContent || message.content || '')
							.split('\n')
							.map((line) => messagePreviewText(line, message.guildId ?? null))
							.join('\n');
						// Its files and pictures come along, shown with the quote.
						const files = messageAgentSources(message);
						const mine = message.author?.id === Users.getCurrentUser()?.id;
						const send = () =>
							void sendToAgent({
								kind: 'message',
								// A 1:1 DM is named after the other person: just their name.
								label: place && place !== author ? i18n._(KONVO_FROM_IN_CHANNEL_DESCRIPTOR, {author, place}) : author,
								text,
								hints: [channel?.name ?? '', guild?.name ?? '', effectiveContent || message.content || ''],
								// Reply to … goes back to the author (to the chat when it was your own message).
								source: {
									kind: 'message',
									channelId: message.channelId,
									guildId: message.guildId ?? null,
									messageId: message.id,
									name: (mine ? place : author).slice(0, 200),
									text: text.slice(0, 4000),
								},
								...(files.length ? {files: fetchAgentFiles(files), fileNames: files.map((file) => file.name)} : {}),
							});
						if (onClose) ModalCommands.runAfterBottomSheetClose(onClose, send);
						else send();
					},
				});
			}
			if (message.isUserMessage()) {
				managementActions.push({
					id: messageActionMenuItemIds.addToScratchpad,
					icon: <ScratchpadIcon size={20} data-flx="channel.message-action-menu.groups.konvo-scratchpad-icon" />,
					label: i18n._(KONVO_ADD_TO_SCRATCHPAD_DESCRIPTOR),
					onClick: () => {
						// One to-do line: the message text (first line, trimmed) with a jump link back to it.
						const text = (effectiveContent || message.content || '').split('\n')[0].trim().slice(0, 200);
						const origin = window.location.origin;
						const path = Navigation.buildMessagePath(message.guildId, message.channelId, message.id);
						const authorName = message.author?.username ?? 'someone';
						KonvoScratchpad.appendToPad(`- [ ] ${text || 'Message'} ([from ${authorName}](${origin}${path}))`);
						KonvoScratchpad.setOpen(true);
						onClose?.();
					},
				});
			}
			if (message.isUserMessage()) {
				managementActions.push({
					id: messageActionMenuItemIds.addToChatPad,
					icon: <ScratchpadIcon size={20} data-flx="channel.message-action-menu.groups.konvo-chat-pad-icon" />,
					label: i18n._(KONVO_ADD_TO_CHAT_PAD_DESCRIPTOR),
					onClick: () => {
						const text = (effectiveContent || message.content || '').split('\n')[0].trim().slice(0, 200);
						const origin = window.location.origin;
						const path = Navigation.buildMessagePath(message.guildId, message.channelId, message.id);
						const authorName = message.author?.username ?? 'someone';
						const pad = KonvoChannelScratchpad.get(message.channelId);
						const append = () => {
							const current = pad.padContent.replace(/\s+$/, '');
							const line = `- [ ] ${text || 'Message'} ([from ${authorName}](${origin}${path}))`;
							pad.setPadContent(current ? `${current}\n${line}` : line);
							KonvoScratchpad.openShared(message.channelId);
						};
						if (pad.loaded) append();
						else void pad.load().then(append);
						onClose?.();
					},
				});
			}
			if (message.isUserMessage()) {
				managementActions.push({
					id: messageActionMenuItemIds.remindMe,
					icon: <BellRingingIcon size={20} data-flx="channel.message-action-menu.groups.konvo-remind-me-icon" />,
					label: i18n._(KONVO_REMIND_ME_DESCRIPTOR),
					onClick: () => {
						const openRemindModal = () => {
							ModalCommands.push(
								modal(() => <KonvoRemindMeModal message={message} onClose={() => ModalCommands.pop()} />),
							);
						};
						if (onClose) {
							ModalCommands.runAfterBottomSheetClose(onClose, openRemindModal);
						} else {
							openRemindModal();
						}
					},
				});
			}
			if (permissions?.shouldRenderSuppressEmbeds) {
				managementActions.push({
					id: messageActionMenuItemIds.suppressEmbeds,
					icon: <SuppressEmbedsIcon size={20} data-flx="channel.message-action-menu.groups.suppress-embeds-icon" />,
					label: isEmbedsSuppressed(message)
						? i18n._(UNSUPPRESS_EMBEDS_DESCRIPTOR)
						: i18n._(SUPPRESS_EMBEDS_DESCRIPTOR),
					onClick: handlers.handleToggleSuppressEmbeds,
					shortcut: (
						<KeybindHint action="message_toggle_embeds" data-flx="channel.message-action-menu.groups.keybind-hint--8" />
					),
				});
			}
			if (permissions?.canDeleteMessage && onDelete) {
				managementActions.push({
					id: messageActionMenuItemIds.deleteMessage,
					icon: <DeleteIcon size={20} data-flx="channel.message-action-menu.groups.delete-icon" />,
					label: i18n._(DELETE_MESSAGE_DESCRIPTOR),
					onClick: (event: {shiftKey?: boolean}) => {
						const deleteMessage = () => onDelete(event?.shiftKey === true);
						if (onClose) {
							ModalCommands.runAfterBottomSheetClose(onClose, deleteMessage);
							return;
						}
						deleteMessage();
					},
					danger: true,
					shortcut: (
						<KeybindHint action="message_delete" data-flx="channel.message-action-menu.groups.keybind-hint--9" />
					),
				});
			}
			if (supportsInteractiveActions) {
				utilityActions.push({
					id: messageActionMenuItemIds.copyMessageLink,
					icon: <CopyLinkIcon size={20} data-flx="channel.message-action-menu.groups.copy-link-icon" />,
					label: i18n._(COPY_MESSAGE_LINK_DESCRIPTOR),
					onClick: handlers.handleCopyMessageLink,
					shortcut: (
						<KeybindHint action="message_copy_link" data-flx="channel.message-action-menu.groups.keybind-hint--10" />
					),
				});
			}
			if (copyableMessageText) {
				utilityActions.push({
					id: messageActionMenuItemIds.copyMessage,
					icon: <CopyMessageTextIcon size={20} data-flx="channel.message-action-menu.groups.copy-message-text-icon" />,
					label: i18n._(COPY_MESSAGE_DESCRIPTOR),
					quickLabel: i18n._(COPY_SHORT_DESCRIPTOR),
					onClick: handlers.handleCopyMessage,
					shortcut: (
						<KeybindHint action="message_copy_text" data-flx="channel.message-action-menu.groups.keybind-hint--11" />
					),
				});
			}
			if (TtsUtils.isSupported() && voiceReady && effectiveContent.trim()) {
				utilityActions.push({
					id: messageActionMenuItemIds.speakMessage,
					icon: isSpeaking ? (
						<StopSpeakingIcon size={20} data-flx="channel.message-action-menu.groups.stop-speaking-icon" />
					) : (
						<SpeakMessageIcon size={20} data-flx="channel.message-action-menu.groups.speak-message-icon" />
					),
					label: isSpeaking ? i18n._(STOP_SPEAKING_DESCRIPTOR) : i18n._(SPEAK_MESSAGE_DESCRIPTOR),
					onClick: handleSpeakMessage,
					closeOnSelect: false,
					shortcut: (
						<KeybindHint action="message_speak" data-flx="channel.message-action-menu.groups.keybind-hint--12" />
					),
				});
			}
			utilityActions.push({
				id: messageActionMenuItemIds.copyMessageId,
				icon: <CopyIdIcon size={20} data-flx="channel.message-action-menu.groups.copy-id-icon" />,
				label: i18n._(COPY_MESSAGE_ID_DESCRIPTOR),
				onClick: handlers.handleCopyMessageId,
				shortcut: (
					<KeybindHint action="message_copy_id" data-flx="channel.message-action-menu.groups.keybind-hint--13" />
				),
			});
			if (developerMode) {
				utilityActions.push({
					id: messageActionMenuItemIds.debugMessage,
					icon: <DebugMessageIcon size={20} data-flx="channel.message-action-menu.groups.debug-message-icon" />,
					label: i18n._(DEBUG_MESSAGE_DESCRIPTOR),
					onClick: handleDebugMessage,
				});
			}
		} else if (message.state === MessageStates.FAILED) {
			interactionActions.push({
				icon: <RetryIcon size={20} data-flx="channel.message-action-menu.groups.retry-icon" />,
				label: i18n._(TRY_AGAIN_DESCRIPTOR),
				onClick: handlers.handleRetryMessage,
			});
			managementActions.push({
				id: messageActionMenuItemIds.deleteMessage,
				icon: <DeleteIcon size={20} data-flx="channel.message-action-menu.groups.delete-icon--2" />,
				label: i18n._(DELETE_MESSAGE_DESCRIPTOR),
				onClick: handlers.handleFailedMessageDelete,
				danger: true,
			});
		}
		// Konvo's own actions and ClickUp's each get a titled section with the logo, so it is clear
		// which integration an action belongs to (the desktop menu labels them the same way).
		const inSection = (ids: ReadonlyArray<string>) =>
			managementActions.filter((item) => 'id' in item && item.id != null && ids.includes(item.id));
		const konvoActions = inSection(KONVO_SECTION_IDS);
		const clickUpActions = inSection(CLICKUP_SECTION_IDS);
		const otherManagement = managementActions.filter(
			(item) => !konvoActions.includes(item) && !clickUpActions.includes(item),
		);
		const groups: Array<MenuGroupType> = [
			{items: reactionActions},
			{items: interactionActions},
			{items: otherManagement},
			{items: konvoActions, title: 'Konvo', titleIcon: <KonvoLogo size={16} />},
			{items: clickUpActions, title: 'ClickUp', titleIcon: <ClickUpLogo size={16} />},
			{items: utilityActions},
		];
		if (canReportMessage(message)) {
			groups.push({
				items: [
					{
						id: messageActionMenuItemIds.reportMessage,
						icon: <ReportMessageIcon size={20} data-flx="channel.message-action-menu.groups.report-message-icon" />,
						label: i18n._(REPORT_MESSAGE_DESCRIPTOR),
						onClick: handleReportMessage,
						danger: true,
					},
				],
			});
		}
		return groups;
	}, [
		message,
		handlers,
		isSaved,
		onClose,
		onDelete,
		onOpenEmojiPicker,
		onOpenReactionsSheet,
		reactions.length,
		permissions,
		developerMode,
		canManageMessages,
		supportsInteractiveActions,
		isSpeaking,
		voiceReady,
		effectiveContent,
		copyableMessageText,
		handleSpeakMessage,
		handleReportMessage,
		handleDebugMessage,
		channel,
		assistantAvailable,
		agentsOn,
		askInSheet,
		i18n.locale,
	]);
	const quickReactionRowVisible =
		permissions?.canAddReactions === true && message.state === MessageStates.SENT && quickReactionEmojis.length > 0;
	return {
		handlers,
		permissions,
		groups,
		quickReactionEmojis,
		submenuReactionEmojis,
		quickReactionRowVisible,
		isFailed: message.state === MessageStates.FAILED,
		isSaved,
	};
};
